import { useEffect, useState } from 'react'
import Icon from '../../common/Icon'
import useAudioStore from '../../../shared/store/audio.store.tsx'
import { useShallow } from 'zustand/react/shallow'
import { formatSecondsToMinutesSeconds } from '../../../utils/formatters.ts'

interface Props {
  file: File
}

export default function MediaFileUploadBox({ file }: Props) {
  const [audioBuffer, setAudioBuffer] = useState<AudioBuffer | null>(null)
  const tmpId = crypto.randomUUID()

  const { isPlaying, playAudioBuffer, pausePlayback, audioContext } =
    useAudioStore(
      useShallow((state) => ({
        isPlaying: state.isPlaying,
        playAudioBuffer: state.playAudioBuffer,
        pausePlayback: state.pausePlayback,
        audioContext: state.audioContext,
      }))
    )

  useEffect(() => {
    const loadAudioBuffer = async () => {
      const arrayBuffer = await file.arrayBuffer()
      const decoded = await audioContext.decodeAudioData(arrayBuffer)
      setAudioBuffer(decoded)
    }

    void loadAudioBuffer()
  }, [file, audioContext])

  const handlePlayPause = () => {
    if (!audioBuffer) return

    if (isPlaying) {
      pausePlayback()
    } else {
      playAudioBuffer(tmpId, audioBuffer)
    }
  }

  return (
    <div className="flex w-full flex-col gap-3 rounded-r-sm bg-zinc-700 p-4 shadow-[0px_0px_8px_-2px_rgba(0,0,0,0.20)]">
      <div className="inline-flex items-start justify-start gap-2 self-stretch">
        <div className="flex shrink grow basis-0 items-center justify-center gap-2">
          <div className="text-md shrink grow basis-0 font-semibold leading-6 text-white">
            {file.name}
          </div>
          <div className="text-right text-sm font-normal leading-tight text-neutral-400">
            {audioBuffer
              ? formatSecondsToMinutesSeconds(audioBuffer?.duration)
              : ''}
          </div>
          <button
            className={`inline-flex h-8 w-8 items-center justify-center gap-1.5 rounded-sm border border-neutral-900 bg-zinc-600 p-1 ${!audioBuffer && 'animate-[pulse_1s_ease-in-out_infinite]'}`}
            onClick={handlePlayPause}
            disabled={!audioBuffer}
          >
            <Icon name={isPlaying ? 'stop' : 'play'} width={32} height={32} />
          </button>
        </div>
      </div>
    </div>
  )
}
